import { useState, useEffect, useRef, useCallback } from "react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { acquireApiToken } from '@/auth/acquireApiToken';

export interface Event {
  id: string;
  business_id: string;
  business_name: string;
  business_logo: string | null;
  business_timezone: string | null;
  title: string;
  description: string | null;
  event_date: string;
  start_time: string;
  end_time: string | null;
  theme: string | null;
  image_url: string | null;
  status: "upcoming" | "live";
  is_recurring: boolean;
  series_id: string | null;
  recurrence_pattern: string | null;
  /** JSON array in a varchar column, so it arrives as a string. */
  recurrence_days: string | string[] | null;
  series_end_date: string | null;
  cover_price: number | null;
  created_at: string;
  updated_at: string;
}

interface EventsResponse {
  items: Event[];
  count: number;
  total: number;
}

export interface UseEventsOptions {
  search?: string;
  page?: number;
  limit?: number;
  apiUrl?: string;
  /** Drill into one series' occurrences. */
  seriesId?: string | null;
  /** Collapse recurring events to one row per series (the monitor view). */
  groupBySeries?: boolean;
}

/** One row per recurring series, from ?groupBy=series.
 *  Events have no template row, so these are aggregates over the occurrences. */
export interface EventSeries {
  series_id: string;
  id: string;
  business_id: string;
  business_name: string;
  business_logo: string | null;
  business_timezone: string | null;
  title: string;
  description: string | null;
  theme: string | null;
  image_url: string | null;
  recurrence_pattern: string | null;
  recurrence_days: string | string[] | null;
  series_end_date: string | null;
  occurrences_total: number;
  occurrences_upcoming: number;
  first_occurrence: string;
  next_occurrence: string | null;
  last_occurrence: string;
}

interface UseEventsResult {
  events: Event[];
  total: number;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useEvents(opts: UseEventsOptions = {}): UseEventsResult {
  const {
    search = "", page = 0, limit = 25, apiUrl,
    seriesId = null, groupBySeries = false,
  } = opts;
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();
  const [events, setEvents] = useState<Event[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const accountsRef = useRef(accounts);
  accountsRef.current = accounts;
  const [refreshTick, setRefreshTick] = useState(0);

  const refetch = useCallback(() => setRefreshTick((t) => t + 1), []);

  useEffect(() => {
    let isMounted = true;

    const fetchEvents = async () => {
      const url = apiUrl || process.env.REACT_APP_API_URL;
      if (!url) {
        if (isMounted) {
          setError("REACT_APP_API_URL is not set");
          setLoading(false);
        }
        return;
      }

      if (isMounted) setLoading(true);

      try {
        let token: string | null = null;
        if (isAuthenticated && accountsRef.current.length > 0) {
          token = await acquireApiToken(instance, accountsRef.current[0]);
        }

        const offset = page * limit;
        const params = new URLSearchParams({
          limit: String(limit),
          offset: String(offset),
        });
        if (search) params.set("search", search);
        if (seriesId) params.set("seriesId", seriesId);
        if (groupBySeries) params.set("groupBy", "series");

        const response = await fetch(`${url}/admin/events?${params}`, {
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        const data: EventsResponse = await response.json();
        if (!response.ok)
          throw new Error((data as any).error || `HTTP ${response.status}`);

        if (isMounted) {
          setEvents(data.items);
          setTotal(data.total);
          setError(null);
        }
      } catch (err: any) {
        if (isMounted) {
          setError(err?.message ?? "Unknown error");
          setEvents([]);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchEvents();
    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, instance, search, page, limit, refreshTick, apiUrl,
      // Without these, switching view or drilling into a series would
      // reuse the previous fetch's results.
      seriesId, groupBySeries]);

  return { events, total, loading, error, refetch };
}
